import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { clearAuth } from "@/lib/auth";
import api from "@/lib/api";
import NotificationBell from "@/components/admin/NotificationBell";
import {
  LayoutDashboard, Users, Briefcase, Calendar, MessageSquare, FileText, Settings, LogOut, Menu, X,
  FileSignature, ListChecks, Receipt, Building2, BookOpen, Scale, Repeat, Activity, BarChart3, Mail,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3a2b13f5-84ee-42a0-a8c4-4ac266af26a7/artifacts/8jhfiwyn_BESSONSOLUTION-logo-avatar.png";

const GROUPS = [
  {
    title: "Pilotage",
    items: [
      { to: "/admin/dashboard", icon: LayoutDashboard, label: "Tableau de bord" },
      { to: "/admin/analytics",  icon: BarChart3,       label: "Analytics & trafic" },
      { to: "/admin/leads",     icon: Users,           label: "Leads (Kanban)" },
      { to: "/admin/projets",   icon: Briefcase,       label: "Projets (Kanban)" },
      { to: "/admin/rendez-vous", icon: Calendar,      label: "Planning" },
      { to: "/admin/types-rdv",   icon: Calendar,      label: "Types de RDV & tarifs" },
    ],
  },
  {
    title: "Commercial",
    items: [
      { to: "/admin/entrepreneurs", icon: Building2,     label: "Clients" },
      { to: "/admin/devis",     icon: FileSignature,   label: "Devis & propositions" },
      { to: "/admin/factures",  icon: Receipt,         label: "Factures" },
      { to: "/admin/abonnements", icon: Repeat,        label: "Abonnements & MRR" },
      { to: "/admin/services",  icon: ListChecks,      label: "Catalogue services" },
    ],
  },
  {
    title: "Contenu",
    items: [
      { to: "/admin/legal",     icon: Scale,           label: "Textes légaux" },
      { to: "/admin/etudes-de-cas", icon: BookOpen,    label: "Études de cas" },
      { to: "/admin/temoignages",   icon: MessageSquare, label: "Avis & témoignages" },
    ],
  },
  {
    title: "Configuration",
    items: [
      { to: "/admin/parametres", icon: Settings,       label: "Paramètres" },
      { to: "/admin/diagnostic", icon: Activity,       label: "Diagnostic & comms" },
      { to: "/admin/emails",    icon: Mail,            label: "Archives emails" },
    ],
  },
];

export default function AdminLayout() {
  const loc = useLocation();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/admin/me").then((r) => setMe(r.data)).catch(() => {
      clearAuth(); nav("/admin/login");
    });
  }, [nav]);

  const logout = () => { clearAuth(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F4FA] via-[#F4F6FA] to-[#E8F0FA] relative overflow-x-clip" data-testid="admin-layout">
      {/* Animated decorative curves — float gently behind the content */}
      <DecorativeCurves />

      {/* Mobile top bar */}
      <header className="lg:hidden relative z-20 grid grid-cols-3 items-center p-3 bg-white/90 backdrop-blur-md border-b border-black/8 sticky top-0">
        <div className="flex justify-start">
          <button onClick={() => setOpen((o) => !o)} className="p-2 rounded-xl hover:bg-black/5 transition-colors" data-testid="admin-mobile-menu" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        <div className="flex justify-center">
          <BackOfficeMark compact />
        </div>
        <div className="flex justify-end">
          <div className="bg-[#0A1128] rounded-xl"><NotificationBell variant="mobile" /></div>
        </div>
      </header>

      <div className="flex relative z-10">
        {/* Sidebar */}
        <aside
          className={`${open ? "block" : "hidden"} lg:block fixed lg:sticky top-0 left-0 z-30 w-72 h-screen overflow-hidden`}
        >
          {/* Sidebar background with dark gradient + subtle curves */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128] via-[#0F1A38] to-[#0A1128]" />
          <div className="absolute -top-32 -right-20 w-72 h-72 rounded-full opacity-25 pointer-events-none" style={{ background: "#06B6D4", filter: "blur(80px)" }} />
          <div className="absolute -bottom-32 -left-20 w-72 h-72 rounded-full opacity-15 pointer-events-none" style={{ background: "#FFDCA8", filter: "blur(90px)" }} />

          <div className="relative h-full flex flex-col text-white p-5 overflow-y-auto">
            {/* Logo + brand */}
            <Link to="/admin" className="hidden lg:flex items-center gap-3 pb-5 border-b border-white/10 group">
              <img src={LOGO_URL} className="w-12 h-12 rounded-xl shadow-lg group-hover:scale-105 transition-transform" alt="BESSON" />
              <div>
                <div className="display font-black text-base leading-none">BESSON</div>
                <div className="display font-black text-base leading-none text-[#67E8F9] -mt-0.5">SOLUTIONS</div>
                <div className="text-[9px] tracking-[0.32em] text-white/55 uppercase mt-1">Back-office</div>
              </div>
            </Link>

            <nav className="mt-5 flex-1 space-y-5">
              {GROUPS.map((g) => (
                <div key={g.title}>
                  <div className="text-[10px] font-bold tracking-[0.32em] text-white/40 uppercase px-3 mb-2">{g.title}</div>
                  <div className="space-y-0.5">
                    {g.items.map((it) => {
                      const active = loc.pathname === it.to || (it.to === "/admin/dashboard" && loc.pathname === "/admin");
                      const slug = it.label.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "");
                      return (
                        <Link
                          key={it.to}
                          to={it.to}
                          data-testid={`admin-nav-${slug}`}
                          onClick={() => setOpen(false)}
                          className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all ${
                            active
                              ? "bg-white text-[#0A1128] shadow-lg shadow-black/20 font-bold"
                              : "text-white/75 hover:bg-white/8 hover:text-white"
                          }`}
                        >
                          {/* Left accent bar when active */}
                          {active && (
                            <span className="absolute -left-5 top-1/2 -translate-y-1/2 w-1.5 h-7 rounded-r-full bg-[#06B6D4] shadow-[0_0_20px_#06B6D4]" />
                          )}
                          <it.icon size={17} className={active ? "text-[#06B6D4]" : "opacity-80 group-hover:opacity-100"} />
                          <span className="truncate">{it.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* Footer user block */}
            <div className="mt-6 pt-5 border-t border-white/10">
              <div className="flex items-center gap-3 mb-2">
                {me?.avatar_url ? (
                  <img src={me.avatar_url} alt="" className="w-11 h-11 rounded-full object-cover border border-white/20" data-testid="admin-sidebar-avatar" />
                ) : (
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#06B6D4] text-[#0A1128] font-extrabold text-base">
                    {((me?.name || "A").trim().slice(0, 1) || "A").toUpperCase()}
                  </span>
                )}
                <div className="min-w-0">
                  <div className="display text-sm font-bold leading-tight truncate">{me?.name || "Admin"}</div>
                  {me?.title && <div className="text-[10px] text-[#67E8F9] uppercase tracking-widest leading-none mt-0.5 truncate">{me.title}</div>}
                  <div className="text-[10px] text-white/55 leading-none mt-0.5 truncate">{me?.email}</div>
                </div>
              </div>
              <button
                onClick={logout}
                data-testid="admin-logout"
                className="mt-1 w-full inline-flex items-center justify-center gap-2 bg-white/8 hover:bg-white/15 border border-white/10 text-white text-[12px] font-bold rounded-xl px-3 py-2.5 transition-colors"
              >
                <LogOut size={13} /> Déconnexion
              </button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 max-w-full">
          {/* Desktop header bar with centered "B ack office" mark */}
          <div className="hidden lg:flex sticky top-0 z-20 items-center justify-between px-4 py-2 bg-white/70 backdrop-blur-xl border-b border-black/5 relative overflow-hidden">
            <SoftCurveBar />
            <div className="relative w-32" /> {/* spacer for balance */}
            <BackOfficeMark compact />
            <div className="relative flex items-center gap-3">
              <div className="bg-[#0A1128] rounded-xl"><NotificationBell /></div>
            </div>
          </div>

          <div className="px-3 py-3 sm:px-4 sm:py-4 relative">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

/** "B" logo + "ack office" — together reads "Back office" with the logo acting as the B */
function BackOfficeMark({ compact = false }) {
  const size = compact ? 36 : 46;
  return (
    <div className="relative flex items-center gap-1.5" data-testid="back-office-mark">
      <img
        src={LOGO_URL}
        alt="B"
        style={{ width: size, height: size }}
        className="rounded-xl shadow-md ring-2 ring-[#06B6D4]/30 transition-transform hover:scale-105"
      />
      <span
        className={`display font-black tracking-tight ${compact ? "text-lg" : "text-2xl"} text-[#0A1128] leading-none`}
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        ack <span className="text-[#06B6D4]">office</span>
      </span>
    </div>
  );
}

/** Soft animated curve sweeping across the top header bar */
function SoftCurveBar() {
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none opacity-50"
      viewBox="0 0 1200 80"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="hdrGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#06B6D4" stopOpacity="0" />
          <stop offset="50%" stopColor="#06B6D4" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#FFDCA8" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M 0 60 Q 300 10 600 45 T 1200 35 L 1200 80 L 0 80 Z"
        fill="url(#hdrGrad)"
      >
        <animate
          attributeName="d"
          dur="14s"
          repeatCount="indefinite"
          values="
            M 0 60 Q 300 10 600 45 T 1200 35 L 1200 80 L 0 80 Z;
            M 0 50 Q 300 70 600 30 T 1200 55 L 1200 80 L 0 80 Z;
            M 0 60 Q 300 10 600 45 T 1200 35 L 1200 80 L 0 80 Z
          "
        />
      </path>
    </svg>
  );
}

/** Floating animated SVG blobs in the page background — soft, curvy, never straight */
function DecorativeCurves() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      <svg className="absolute -top-32 right-[-10%] w-[600px] h-[600px] opacity-25 animate-blob-slow" viewBox="0 0 200 200">
        <defs>
          <linearGradient id="blob1" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#06B6D4" />
            <stop offset="100%" stopColor="#A7F3D0" />
          </linearGradient>
        </defs>
        <path fill="url(#blob1)">
          <animate attributeName="d" dur="22s" repeatCount="indefinite"
            values="
              M50,-58C63,-48,71,-30,73,-12C75,6,71,23,60,38C49,53,30,65,11,68C-9,72,-29,67,-45,55C-61,43,-72,25,-73,7C-74,-12,-65,-32,-50,-44C-35,-56,-15,-60,3,-63C21,-65,38,-67,50,-58Z;
              M44,-52C58,-44,72,-32,74,-17C77,-2,69,15,58,30C47,46,33,60,15,66C-3,72,-26,70,-44,59C-62,48,-76,28,-77,7C-78,-15,-66,-37,-49,-49C-32,-61,-11,-62,7,-67C25,-72,30,-60,44,-52Z;
              M50,-58C63,-48,71,-30,73,-12C75,6,71,23,60,38C49,53,30,65,11,68C-9,72,-29,67,-45,55C-61,43,-72,25,-73,7C-74,-12,-65,-32,-50,-44C-35,-56,-15,-60,3,-63C21,-65,38,-67,50,-58Z
            "
          />
        </path>
      </svg>
      <svg className="absolute bottom-[-15%] left-[35%] w-[520px] h-[520px] opacity-20 animate-blob-slower" viewBox="0 0 200 200">
        <defs>
          <linearGradient id="blob2" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FFDCA8" />
            <stop offset="100%" stopColor="#67E8F9" />
          </linearGradient>
        </defs>
        <path fill="url(#blob2)">
          <animate attributeName="d" dur="28s" repeatCount="indefinite"
            values="
              M40,-46C53,-37,66,-28,71,-15C76,-2,73,16,63,29C53,42,37,50,21,57C5,64,-12,69,-30,64C-48,59,-67,43,-73,24C-79,5,-72,-17,-60,-32C-48,-47,-31,-55,-14,-58C2,-61,21,-58,40,-46Z;
              M50,-55C63,-44,71,-26,71,-9C71,8,63,25,52,40C41,55,26,68,7,72C-12,76,-35,71,-52,59C-69,47,-79,28,-76,9C-73,-11,-58,-29,-44,-42C-30,-55,-17,-63,1,-65C19,-67,37,-66,50,-55Z;
              M40,-46C53,-37,66,-28,71,-15C76,-2,73,16,63,29C53,42,37,50,21,57C5,64,-12,69,-30,64C-48,59,-67,43,-73,24C-79,5,-72,-17,-60,-32C-48,-47,-31,-55,-14,-58C2,-61,21,-58,40,-46Z
            "
          />
        </path>
      </svg>
    </div>
  );
}
